/**
 * useTaskEnrichments · batch fetch de TODO lo que enriquece visualmente
 * una card del kanban / row del list.
 *
 * Carga en paralelo y devuelve un Map<task_id, enrichment> con:
 *  - labels (hashtags)
 *  - assignments (avatares apilados)
 *  - subtask counts (subtask progress bar)
 *  - prospect name (relation chip)
 *  - project name (relation chip)
 *
 * Diseñado para alimentar Kanban + List + Calendar + Mine + Inbox sin
 * duplicar queries: cada vista pasa sus taskIds visibles y recibe el
 * mismo enrichment shape.
 */
import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import { useUsersBasicInfo } from '@/features/chat/hooks';
import type { TasksRow } from '@/types/database.types';

import * as labelsApi from '../api/taskLabels';

export interface TaskEnrichment {
  labels: Array<{ id: string; name: string; slug: string; color: string | null }>;
  assigneeIds: string[];
  ownerId: string | null;
  watcherIds: string[];
  subtaskTotal: number;
  subtaskDone: number;
  prospectName: string | null;
  projectName: string | null;
}

const EMPTY: TaskEnrichment = {
  labels: [],
  assigneeIds: [],
  ownerId: null,
  watcherIds: [],
  subtaskTotal: 0,
  subtaskDone: 0,
  prospectName: null,
  projectName: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

interface BatchEnrichmentResult {
  byId: Map<string, TaskEnrichment>;
  userIds: string[];
}

/**
 * Hook que devuelve enrichments por task + el set de userIds presentes,
 * para que la vista hidrate avatares con useUsersBasicInfo en un solo fetch.
 */
export function useTaskEnrichments(tasks: TasksRow[]) {
  const taskIds = tasks.map((t) => t.id);
  const taskIdsKey = [...taskIds].sort().join(',');

  const parentIds = tasks.filter((t) => t.parent_task_id).map((t) => t.parent_task_id!);
  const prospectIds = [...new Set(tasks.map((t) => t.prospect_id).filter(Boolean) as string[])];
  const projectIds = [...new Set(tasks.map((t) => t.project_id).filter(Boolean) as string[])];

  const enrichQuery = useQuery({
    queryKey: ['core', 'task-enrichments', taskIdsKey],
    enabled: taskIds.length > 0,
    queryFn: async (): Promise<BatchEnrichmentResult> => {
      const byId = new Map<string, TaskEnrichment>();
      for (const id of taskIds) byId.set(id, { ...EMPTY, labels: [], assigneeIds: [], watcherIds: [] });

      // --- En paralelo ---
      const [labelsRes, assignmentsRes, subtasksRes, prospectsRes, projectsRes] = await Promise.all([
        labelsApi.listLabelsForTasks(taskIds),
        // assignments batch
        db
          .from('task_assignments')
          .select('task_id, user_id, role')
          .in('task_id', taskIds)
          .is('declined', false),
        // subtask counts: SELECT parent_task_id, status FROM tasks WHERE parent_task_id IN ...
        taskIds.length > 0
          ? db
              .from('tasks')
              .select('parent_task_id, status')
              .in('parent_task_id', taskIds)
              .is('deleted_at', null)
          : { data: [], error: null },
        prospectIds.length > 0
          ? supabase.from('prospects').select('id, name').in('id', prospectIds)
          : { data: [], error: null },
        projectIds.length > 0
          ? supabase.from('projects').select('id, name').in('id', projectIds)
          : { data: [], error: null },
      ]);

      // Labels
      for (const link of labelsRes) {
        const e = byId.get(link.task_id);
        if (e) e.labels.push(link.label);
      }

      // Assignments
      type AssignmentRow = { task_id: string; user_id: string; role: 'owner' | 'assignee' | 'watcher' };
      const assignments = ((assignmentsRes as { data: AssignmentRow[] | null }).data ?? []) as AssignmentRow[];
      for (const a of assignments) {
        const e = byId.get(a.task_id);
        if (!e) continue;
        if (a.role === 'owner') e.ownerId = a.user_id;
        else if (a.role === 'watcher') e.watcherIds.push(a.user_id);
        else e.assigneeIds.push(a.user_id);
      }

      // Subtask counts
      type SubtaskRow = { parent_task_id: string | null; status: string };
      const subtasks = ((subtasksRes as { data: SubtaskRow[] | null }).data ?? []) as SubtaskRow[];
      for (const s of subtasks) {
        if (!s.parent_task_id) continue;
        const e = byId.get(s.parent_task_id);
        if (!e) continue;
        e.subtaskTotal += 1;
        if (s.status === 'done') e.subtaskDone += 1;
      }

      // Prospect / project names
      type IdName = { id: string; name: string };
      const prospects = ((prospectsRes as { data: IdName[] | null }).data ?? []) as IdName[];
      const prospectMap = new Map(prospects.map((p) => [p.id, p.name]));
      const projects = ((projectsRes as { data: IdName[] | null }).data ?? []) as IdName[];
      const projectMap = new Map(projects.map((p) => [p.id, p.name]));
      for (const t of tasks) {
        const e = byId.get(t.id);
        if (!e) continue;
        if (t.prospect_id) e.prospectName = prospectMap.get(t.prospect_id) ?? null;
        if (t.project_id) e.projectName = projectMap.get(t.project_id) ?? null;
      }

      // userIds set para hidratar useUsersBasicInfo
      const userIds = new Set<string>();
      for (const e of byId.values()) {
        if (e.ownerId) userIds.add(e.ownerId);
        for (const id of e.assigneeIds) userIds.add(id);
        for (const id of e.watcherIds) userIds.add(id);
      }

      return { byId, userIds: [...userIds] };
    },
    staleTime: 30_000,
  });

  // Resolver display_name + avatar_url de todos los users mencionados
  const usersQuery = useUsersBasicInfo(enrichQuery.data?.userIds ?? []);

  return {
    enrichments: enrichQuery.data?.byId,
    users: usersQuery.data,
    isLoading: enrichQuery.isLoading,
    parentIds,
  };
}
